import CSVFileValidator, { FieldSchema, RowError } from 'csv-file-validator';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CustomFile } from 'ui/components/Dropzone/types';

import { IMPORT_FILE_MESSAGES } from '~/constants/messages.constants';
import { PAGES } from '~/constants/pages.constants';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useQuery } from '~/hooks/useQuery';
import { useRouter } from '~/hooks/useRouter';
import {
  ACTIVITY_TEMPLATE_GOOGLE_SHEET_URL,
  csvActivityHeadersValidator,
  legacyCsvActivityHeadersValidator,
} from '~/pages/Authenticated/BulkUploads/ActivitiesUpload/utils';
import { EImportFileType } from '~/pages/Authenticated/BulkUploads/types';
import { ImportFilesServices } from '~/services/resources/import-files';
import { UploadsSignedURLObjectTarget } from '~/services/resources/types';

import { useLoadImportProgress } from '../../../../hooks/useLoadImportProgress';
import { fileContainsString } from '../../../../utils/file/fileContainsString';

interface IActivitiesUploadController {
  target: UploadsSignedURLObjectTarget;
  importFileType: EImportFileType;
  mustGetAllImportFiles?: boolean;
}

const PROGRESS_INTERVAL = 3000;

export const useActivitiesUploadController = (
  controllerProps: IActivitiesUploadController,
) => {
  // Providers
  const { control } = useForm();
  const { params, goToRoute } = useRouter();
  const { organizationSelectedId } = useAppSelector(({ auth }) => auth);
  const { selectedEcosystem, ecosystemApps } = useAppSelector(
    ({ ecosystem }) => ecosystem,
  );

  // States
  const [refetchQuantity, setRefetchQuantity] = useState(0);
  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  const [currentFile, setCurrentFile] = useState<CustomFile[]>();
  const [csvValidationErrors, setCsvValidationErrors] = useState<RowError[]>();

  const { data: defaultImportFile, isLoading: isDefaultImportFileLoading } =
    useQuery<any>(`import-file/${params.id}`, {
      queryOptions: {
        enabled: !!params?.id,
      },
    });

  const { data: progressValue } = useLoadImportProgress(
    params.id,
    PROGRESS_INTERVAL,
  );

  const validateCSV = async (
    file: CustomFile,
  ): Promise<{ errors: boolean; isLegacyTemplate: boolean }> => {
    try {
      const isLegacyTemplate = await fileContainsString(
        file,
        'type_of_attendance',
      );

      const validator = isLegacyTemplate
        ? legacyCsvActivityHeadersValidator
        : csvActivityHeadersValidator;

      const { inValidData, data } = await CSVFileValidator(file, {
        headers: validator,
      });

      const onlyHeaderErrors: RowError[] = inValidData.filter(
        ({ rowIndex }) => rowIndex === 1,
      );

      if (data.length === 0) {
        onlyHeaderErrors.push({ message: 'File is empty' });
        return { errors: true, isLegacyTemplate };
      }

      if (onlyHeaderErrors.length > 0) {
        setCsvValidationErrors(onlyHeaderErrors);
        return { errors: true, isLegacyTemplate };
      }

      return { errors: false, isLegacyTemplate };
    } catch (error) {
      console.log(error);
      toast.error('Error on validate file.');
      return { errors: true, isLegacyTemplate: false };
    }
  };

  const uploadActivityFile = async () => {
    setUploadIsLoading(true);
    try {
      const organizationId = organizationSelectedId;
      const publishedApps = control._formValues.appSelect ?? [];

      if (!organizationId || !currentFile?.length || !publishedApps.length) {
        toast.error(IMPORT_FILE_MESSAGES.INPUTS_MUST_NOT_BE_EMPTY);
        return;
      }

      const [file] = currentFile;
      const fileType = file.name.split('.').pop();

      if (fileType !== 'tsv') {
        throw new Error(IMPORT_FILE_MESSAGES.FILE_MUST_BE_TSV_ERROR);
      }
      const { errors, isLegacyTemplate } = await validateCSV(file);
      if (errors) return;

      const newImportFile = await ImportFilesServices.createImportFile({
        organizationId,
        ecosystemId: String(selectedEcosystem?._id),
        app: publishedApps[0],
        publishedApps: publishedApps,
        fileName: file.name,
        importFileType: controllerProps.importFileType,
        isLegacy: isLegacyTemplate,
      });

      const signedURL = await ImportFilesServices.generatePreSignedAdminURL({
        Target: controllerProps.target,
        FileExtension: 'tsv',
        ImportFileId: newImportFile._id,
      });

      await ImportFilesServices.uploadFileToS3({
        file: file,
        signedURL,
      });

      goToRoute(`${PAGES.UPLOADS_ACTIVITIES}/${newImportFile._id}`);
    } catch (error: any) {
      if (error.message === IMPORT_FILE_MESSAGES.FILE_MUST_BE_TSV_ERROR) {
        toast.error(IMPORT_FILE_MESSAGES.FILE_MUST_BE_TSV_ERROR);
        return;
      }
      toast.error(IMPORT_FILE_MESSAGES.UPLOAD_ERROR);
    } finally {
      setUploadIsLoading(false);
    }
  };

  const refreshImports = async () => {
    setRefetchQuantity((prev) => prev + 1);
  };

  const handleDropFile = (
    onChange: (file: CustomFile[]) => void,
    files: CustomFile[],
    isDisabled: boolean,
  ) => {
    if (isDisabled) return;
    onChange(files);
    setCurrentFile(files);
    setCsvValidationErrors(undefined);
  };

  const handleRemoveFile = (isDisabled: boolean) => {
    if (isDisabled) return;
    setCurrentFile(undefined);
    setCsvValidationErrors(undefined);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = ACTIVITY_TEMPLATE_GOOGLE_SHEET_URL;
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
  };

  const appList = useMemo(() => {
    const appListDic = Object.values(ecosystemApps);
    return (appListDic ?? []).map((app) => {
      return { name: app.name, logo: app.logo, value: app._id };
    });
  }, [ecosystemApps]);

  const isViewMode = useMemo(() => {
    if (!params || !params.id) return false;
    return true;
  }, [params]);

  return {
    control,
    currentFile,
    uploadActivityFile,
    refreshImports,
    uploadIsLoading,
    csvValidationErrors,
    defaultImportFile,
    isDefaultImportFileLoading,
    refetchQuantity,
    handleDropFile,
    handleRemoveFile,
    handleDownloadTemplate,
    appList,
    isViewMode,
    goToRoute,
    progressValue,
  };
};
